import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function HelpInfoBubble({
  help,
  variant = 'frame',
  align = 'start',
  ariaLabel,
  resourceKey = 'private.navbar.personalhub.factionsReputations',
  resourceAction = 'read',
  description,
  className = '',
  anchorClassName = '',
  popoverClassName = '',
}) {
  const popoverId = useId();
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const steps = Array.isArray(help?.steps) ? help.steps : [];

  useLayoutEffect(() => {
    if (!open) return undefined;

    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return undefined;

    const place = () => {
      const triggerRect = trigger.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const margin = 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popoverWidth = popoverRect.width || popover.offsetWidth;
      const popoverHeight = popoverRect.height || popover.offsetHeight;

      let top =
        variant === 'frame'
          ? triggerRect.bottom + 4
          : triggerRect.bottom + Math.round(margin * 0.35);
      let left =
        variant === 'frame' || align === 'end'
          ? triggerRect.right - popoverWidth
          : triggerRect.left;

      left = Math.max(margin, Math.min(left, viewportWidth - popoverWidth - margin));
      top = Math.max(margin, Math.min(top, viewportHeight - popoverHeight - margin));

      popover.style.setProperty('top', `${Math.round(top)}px`);
      popover.style.setProperty('left', `${Math.round(left)}px`);
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, variant, align, help]);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      const target = event.target;
      if (wrapRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const wrapClasses = [
    variant === 'frame' ? 'hangar-filters__help-anchor' : 'help-info-bubble help-info-bubble--inline',
    align === 'end' ? 'help-info-bubble--align-end' : '',
    anchorClassName,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const popoverClasses = [
    'accounting-help-popover',
    'accounting-help-popover--portal',
    variant === 'frame' ? 'hangar-filters__help-popover' : 'help-info-bubble__popover',
    popoverClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const popover =
    open && help ? (
      <div
        ref={popoverRef}
        id={popoverId}
        className={popoverClasses}
        role="dialog"
        aria-label={help?.title}
      >
        <div className="accounting-help-popover__accent" aria-hidden="true" />
        <div className="accounting-help-popover__header">
          <h5 className="accounting-help-popover__title">{help.title}</h5>
          <button
            type="button"
            className="accounting-help-popover__close"
            onClick={() => setOpen(false)}
            aria-label="Close help"
          >
            ×
          </button>
        </div>
        {help.summary ? (
          <p className="accounting-help-popover__summary">{help.summary}</p>
        ) : null}
        {steps.length > 0 ? (
          <ul className="accounting-help-popover__steps">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
        ) : null}
        {help.note ? <p className="accounting-help-popover__note">{help.note}</p> : null}
      </div>
    ) : null;

  return (
    <div ref={wrapRef} className={wrapClasses}>
      <button
        ref={triggerRef}
        type="button"
        className={[
          'accounting-help-trigger',
          open ? 'is-open' : '',
          variant === 'inline' ? 'accounting-help-trigger--inline' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={ariaLabel || help?.title || 'Help'}
        data-resource={resourceKey}
        data-action={resourceAction}
        data-description={description}
      >
        <span className="accounting-help-trigger__icon" aria-hidden="true">
          i
        </span>
      </button>

      {typeof document !== 'undefined' && popover
        ? createPortal(popover, document.body)
        : null}
    </div>
  );
}
